import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "../auth/AuthContext";

const wards = [
  "Ward 1 · Shanala",
  "Ward 2 · Sanala Road",
  "Ward 3 · Ravapar",
  "Ward 4 · Mahendra Nagar",
  "Ward 5 · Sardar Nagar",
  "Ward 6 · Lati Plot",
  "Ward 7 · Sanalapar",
  "Ward 8 · Trajpar",
];

export const JoinModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [ward, setWard] = useState(wards[0]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setSubmitting(true);
    setErr(null);
    try {
      await axios.post(
        `${API}/pledges`,
        { name, phone, ward },
        { withCredentials: true }
      );
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setPhone("");
        onClose?.();
      }, 1800);
    } catch (e) {
      setErr("Could not submit — please try again.");
    }
    setSubmitting(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          data-testid="join-modal"
        >
          <div
            onClick={onClose}
            className="absolute inset-0 bg-[color:var(--mati-forest)]/70 backdrop-blur-md"
          />
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg bg-[color:var(--mati-sand)] rounded-3xl border border-[color:var(--mati-border)] overflow-hidden"
          >
            <button
              onClick={onClose}
              data-testid="join-modal-close"
              className="absolute top-5 right-5 p-2 rounded-full border border-[color:var(--mati-border)] hover:bg-[color:var(--mati-forest)] hover:text-[color:var(--mati-sand)] transition"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-8 sm:p-10">
              <div className="eyebrow text-[color:var(--mati-clay)]">
                Civic Pledge
              </div>
              <h3 className="font-display text-3xl sm:text-4xl font-bold text-[color:var(--mati-forest)] mt-3 leading-tight">
                Join the next clean-up drive.
              </h3>
              <p className="mt-3 font-sub text-sm text-[color:var(--mati-muted)]">
                Signed in as{" "}
                <span className="font-semibold text-[color:var(--mati-forest)]">
                  {user?.email}
                </span>
                . Pick your ward — we'll send the next shramdaan schedule.
              </p>

              {!submitted ? (
                <form onSubmit={submit} className="mt-8 space-y-4">
                  <div>
                    <label className="eyebrow text-[color:var(--mati-forest)]">
                      Your Name
                    </label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="join-name-input"
                      className="mt-2 w-full bg-white border border-[color:var(--mati-border)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-[color:var(--mati-forest)]">
                      Phone
                    </label>
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="join-phone-input"
                      className="mt-2 w-full bg-white border border-[color:var(--mati-border)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none"
                      placeholder="+91 98XXXXXXXX"
                      required
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-[color:var(--mati-forest)]">
                      Ward
                    </label>
                    <select
                      value={ward}
                      onChange={(e) => setWard(e.target.value)}
                      data-testid="join-ward-select"
                      className="mt-2 w-full bg-white border border-[color:var(--mati-border)] rounded-xl px-4 py-3 font-sub text-[15px] focus:border-[color:var(--mati-clay)] outline-none"
                    >
                      {wards.map((w) => (
                        <option key={w}>{w}</option>
                      ))}
                    </select>
                  </div>
                  {err && (
                    <div className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-red-600">
                      {err}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="join-submit-btn"
                    className="w-full mt-4 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-clay)] transition-all disabled:opacity-60"
                  >
                    {submitting ? "Pledging…" : "Pledge my participation"}
                  </button>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-10 flex flex-col items-center text-center"
                  data-testid="join-success"
                >
                  <div className="w-14 h-14 rounded-full bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] flex items-center justify-center">
                    <Check className="w-6 h-6" />
                  </div>
                  <div className="font-display text-2xl font-bold text-[color:var(--mati-forest)] mt-4">
                    Pledge received.
                  </div>
                  <p className="font-sub text-sm text-[color:var(--mati-muted)] mt-2">
                    See you on Sunday, {ward.split("·")[1]?.trim() || ward}.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default JoinModal;
